import { FluentProvider } from '@fluentui/react-components';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTeamsHost } from '#src/teams/TeamsHostProvider';
import {
  resolveThemeMode,
  THEME_PREFERENCES,
  THEMES,
  type ThemeMode,
  type ThemePreference,
} from '#src/theme/theme';

const STORAGE_KEY = 'arquivo-morto.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  readonly preference: ThemePreference;
  /** O tema em uso, para as superfícies da identidade saberem quando ceder. */
  readonly mode: ThemeMode;
  setPreference(preference: ThemePreference): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** A preferência é conveniência da pessoa: sem armazenamento, volta a "sistema". */
function readPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return THEME_PREFERENCES.find((p) => p === saved) ?? 'system';
  } catch {
    return 'system';
  }
}

function savePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Sem armazenamento a escolha vale só até recarregar a página.
  }
}

function useSystemPrefersDark(): boolean {
  const [dark, setDark] = useState(() => matchMedia(DARK_QUERY).matches);
  useEffect(() => {
    const query = matchMedia(DARK_QUERY);
    const update = () => setDark(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return dark;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const host = useTeamsHost();
  const [preference, setPreferenceState] = useState(readPreference);
  const [teamsTheme, setTeamsTheme] = useState(host?.theme ?? null);
  const systemPrefersDark = useSystemPrefersDark();

  useEffect(() => host?.onThemeChange(setTeamsTheme), [host]);

  const mode = resolveThemeMode({ preference, teamsTheme, systemPrefersDark });
  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      mode,
      setPreference(next) {
        savePreference(next);
        setPreferenceState(next);
      },
    }),
    [preference, mode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <FluentProvider theme={THEMES[mode]} style={{ height: '100%' }}>
        {children}
      </FluentProvider>
    </ThemeContext.Provider>
  );
}

export function useThemePreference(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error('useThemePreference fora do ThemeProvider');
  }
  return value;
}
